import React, { useCallback, useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useDispatch } from "react-redux";

import { Button, Tooltip } from "@material-ui/core";
import { makeStyles } from '@material-ui/styles';

import makePath from "../../utils/makePath";
import generateId from '../../utils/generateId'
import { deleteItem, reorderListItem } from "../../actions";
import { ListItem } from "./ListItem";
import { usePrevious } from "../../hooks/usePrevious";
import { ConditionalWrapper } from "./ConditionalWrapper";
import { useDirty } from "../../hooks";

const useStyles = makeStyles((theme) => ({
  draggable: {
    position: 'relative',
    userSelect: "none",
  },
  handle: {    
    position: 'absolute',
    left: '22px',
    zIndex: '10',
    top: '14px',
    color: theme.palette.neutral.med
  },
}))

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = [...list];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  // styles we need to apply on draggables
  ...draggableStyle,
  // change opacity if dragging
  opacity: isDragging ? '.7' :'1'
});


export const SortableList =  ({ callback, children, given, index: parentIndex, label, min = 0, max = 50, name, path }) => {
  path = makePath(path,name)

  const classes = useStyles();

  const setDirty = useDirty();
  const dispatch = useDispatch();
  
  const [items, setItems] = useState([]);
  
  const prevGiven = usePrevious(given);

  // make sure list always has minimum number of items
  const fillRemainder = useCallback((parsed) => {
    for (let i = parsed.length; i < min; i++) {
      parsed.push({
        listId: generateId('listItem'),
        content: null,
        given: {}
      })
    }
    return parsed;
  },[min])


  // Seeds values with those provided by parent if applicable;
  // Otherwise, seeds with minimum number of items
  useEffect(() => {
    // we will need to parse any given items to the format required
    // by SortableList: { listId: ..., content: ..., given: ... }
    let parsed = [];

    // Want to change when given is updated. But if given is the same and items are already present, do nothing.
    if (given && given !== prevGiven) {
      parsed = given.map(item => {
        return {
          listId: generateId('listItem'),
          content: item,
          given: item
        }
      })     
      setItems(parsed);
    } else {
      if (parsed.length === 0 && items.length > 0 ) {
        parsed = [...items]
      }

      parsed = fillRemainder(parsed);
      if (parsed.length !== items.length) {
        setItems(parsed);
      }
    }
  }, [given, fillRemainder, items, prevGiven]);

  // Call callback if provided when items update
  useEffect(() => {
    if (callback) {
      callback(items);
    }
  }, [callback,items])


  // Adds new list item to local items and to store
  const addItem = () => {
    const newItem = {
      listId: generateId('listItem'),
      content: null,
      given: {}
    }
    const newItems = [...items, newItem];

    setItems(newItems);
    setDirty(true);
  }

  // Removes list item from local items and from store
  const removeItem = (index) => {
    const newItems = items.filter((item,loopIndex) => loopIndex !== index);
    const itemPath = makePath(path,index);

    setItems(newItems);
    setDirty(true);
    
    dispatch(deleteItem(itemPath));
  }
  
  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (result.type === "droppableItem") {
      let newItems = reorder(items, sourceIndex, destIndex);
      setItems(newItems);
      setDirty(true);

      // The reordered items WILL set themselves automatically when their paths change,
      // but they won't delete any extraneous fields that existed at the previous path
      dispatch(reorderListItem(path, sourceIndex, destIndex))
    } 

  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>

      <Droppable droppableId="droppable" type="droppableItem">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            style={{width: '100%'}}
          >
            {items.map((item, index) => (
              <Draggable key={item.listId} draggableId={item.listId} index={index}>
                {(provided, snapshot) => (
                  <div>
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style
                      )}
                      className={classes.draggable}
                    >
                      <span {...provided.dragHandleProps} className={classes.handle}>
                        <i className="fas fa-ellipsis-v"></i><i className="fas fa-ellipsis-v"></i>
                      </span>

                      {/* ListItem wrapper */}
                      <ListItem 
                        key={item.listId} 
                        listId={item.listId} 
                        label={label}
                        addItem={addItem} 
                        removeItem={removeItem} 
                        index={index} 
                        given={item.given}
                        path={path}
                        disableDelete={items.length <= min}
                      >
                        {/* Component in the list */}
                        {children}
                      </ListItem>

                    </div>
                    {provided.placeholder}
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Add a tooltip wrapper when max is reached - informs the user why button is disabled */}
      <ConditionalWrapper
        condition={items.length >= max}
        wrapper={children => <Tooltip title={`Unable to add. You have reached the max number supported.`} arrow interactive><span>{children}</span></Tooltip>}
      >
        <Button
          color="primary"
          fullWidth
          style={{marginTop: 4}}
          disabled={ items.length >= max }
          onClick={addItem}
        >
          {`+ Add ${label}`}
        </Button>

      </ConditionalWrapper>

    </DragDropContext>
  );
}